/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useCallback } from 'react';
import {
  ShieldCheck, Store, ArrowRight, Users, BarChart3, FileText,
  Truck, PackageCheck, CreditCard, Briefcase, UserPlus, Calendar,
  GraduationCap, Search, Award, ChevronDown
} from 'lucide-react';
import './LandingPage.css';

/* ─── Marquee words ─── */
const MQ = [
  'PROCUREMENT','COMPLIANCE','ANALYTICS','HIRING',
  'VENDORS','TALENT','MANAGEMENT','AUTOMATION',
  'ONBOARDING','REPORTING','CONTRACTS','WORKFLOWS'
];

/* ─── Feature row ─── */
function F({ icon: I, text, t }) {
  return (
    <div className={`lp-f lp-f--${t}`}>
      <span className="lp-fi"><I size={16} /></span>
      <span>{text}</span>
    </div>
  );
}

/* ─── 3D Tilt Card wrapper ─── */
function Tilt({ children, className }) {
  const r = useRef(null);

  const onMove = useCallback((e) => {
    const c = r.current;
    if (!c) return;
    const rc = c.getBoundingClientRect();
    const x = e.clientX - rc.left;
    const y = e.clientY - rc.top;
    const cx = rc.width / 2;
    const cy = rc.height / 2;
    const rx = -(y - cy) / 18;
    const ry = (x - cx) / 18;
    c.style.transform =
      `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg) scale3d(1.03,1.03,1.03)`;
    /* inner spotlight */
    const b = c.querySelector('.lp-card-body');
    if (b) {
      b.style.background =
        `radial-gradient(680px circle at ${((x / rc.width) * 100).toFixed(1)}% ${((y / rc.height) * 100).toFixed(1)}%, rgba(255,255,255,.85), #fff 70%)`;
    }
  }, []);

  const onLeave = useCallback(() => {
    const c = r.current;
    if (!c) return;
    c.style.transition = 'transform .6s cubic-bezier(.33,1,.68,1)';
    c.style.transform = '';
    const b = c.querySelector('.lp-card-body');
    if (b) b.style.background = '';
    setTimeout(() => { if (c) c.style.transition = ''; }, 600);
  }, []);

  const onEnter = useCallback(() => {
    if (r.current) r.current.style.transition = '';
  }, []);

  return (
    <div ref={r} className={className}
      onMouseMove={onMove} onMouseLeave={onLeave} onMouseEnter={onEnter}>
      {children}
    </div>
  );
}

/* ═══════════ MAIN ═══════════ */
export default function LandingPage({
  onNavigateAdmin, onNavigateVendor, onNavigateRecruiter, onNavigateCandidate,
}) {
  const heroRef = useRef(null);
  const partRef = useRef(null);
  const modRef  = useRef(null);

  /* ── Scroll reveals ── */
  useEffect(() => {
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('vis'); io.unobserve(e.target); } }),
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    document.querySelectorAll('.sr').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  /* ── Hero mouse parallax on orbs ── */
  const onMouse = useCallback((e) => {
    if (!heroRef.current) return;
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    const dx = (e.clientX - cx) / cx;
    const dy = (e.clientY - cy) / cy;
    heroRef.current.querySelectorAll('.h-orb').forEach((o, i) => {
      const s = (i + 1) * 16;
      o.style.transform = `translate(${dx * s}px,${dy * s}px)`;
    });
  }, []);
  useEffect(() => {
    window.addEventListener('mousemove', onMouse);
    return () => window.removeEventListener('mousemove', onMouse);
  }, [onMouse]);

  /* ── Particles ── */
  useEffect(() => {
    const c = partRef.current;
    if (!c) return;
    const ps = [];
    for (let i = 0; i < 45; i++) {
      const p = document.createElement('div');
      p.className = 'h-particle';
      const sz = Math.random() * 2.5 + 1;
      p.style.cssText = `left:${Math.random()*100}%;bottom:${Math.random()*-20}%;width:${sz}px;height:${sz}px;animation-duration:${Math.random()*22+14}s;animation-delay:${Math.random()*16}s;`;
      c.appendChild(p);
      ps.push(p);
    }
    return () => ps.forEach((p) => p.remove());
  }, []);

  /* ── Module section spotlight ── */
  const onModMove = useCallback((e) => {
    const s = modRef.current;
    if (!s) return;
    const rc = s.getBoundingClientRect();
    s.style.setProperty('--mx', `${e.clientX - rc.left}px`);
    s.style.setProperty('--my', `${e.clientY - rc.top}px`);
  }, []);

  /* ── Scroll to modules ── */
  const scrollDown = () => {
    document.getElementById('portals')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="lp-w">

      {/* ▸ Intro reveal overlay */}
      <div className="lp-intro" aria-hidden="true">
        <div className="lp-intro-icon"><ShieldCheck size={36} color="#fff" /></div>
        <span className="lp-intro-text">TalentOza</span>
      </div>

      {/* ▸ Nav */}
      <nav className="lp-nav">
        <div className="lp-nav-in">
          <div className="lp-brand">
            <div className="lp-brand-box"><ShieldCheck size={22} color="#fff" /></div>
            <span className="lp-brand-t">TalentOza</span>
          </div>
          <div className="lp-nav-ls">
            <a href="#" className="lp-nl">About</a>
            <a href="#" className="lp-nl">Services</a>
            <a href="#" className="lp-nl lp-nl--a">Contact</a>
          </div>
        </div>
      </nav>

      {/* ▸ Hero */}
      <section className="lp-hero" ref={heroRef}>
        <div className="h-orb h-orb--1" />
        <div className="h-orb h-orb--2" />
        <div className="h-orb h-orb--3" />
        <div className="h-particles" ref={partRef} />
        <div className="h-shapes">
          <div className="h-sh h-sh--1" />
          <div className="h-sh h-sh--2" />
          <div className="h-sh h-sh--3" />
          <div className="h-sh h-sh--4" />
          <div className="h-sh h-sh--5" />
        </div>

        <div className="lp-hero-c">
          <div className="lp-badge sr sr-up d1">
            <span className="lp-badge-dot" />
            Enterprise Platform
          </div>
          <h1 className="lp-hero-h sr sr-up d2">
            Streamlined Vendor &amp;<br />
            <span className="gr-text">Talent Management</span>
          </h1>
          <p className="lp-hero-p sr sr-up d3">
            A unified platform connecting administrators, vendors, and
            recruiters for seamless procurement, compliance, and hiring.
          </p>
          <div className="lp-hero-cta sr sr-up d4">
            <button className="lp-hbtn lp-hbtn--pri" onClick={scrollDown}>
              Explore Portals <ArrowRight size={18} />
            </button>
            <button className="lp-hbtn lp-hbtn--ghost">
              Learn More <ChevronDown size={18} />
            </button>
          </div>
        </div>

        <div className="lp-scroll sr sr-up d5">
          <div className="lp-scroll-m"><div className="lp-scroll-d" /></div>
          <span>Scroll to explore</span>
        </div>

        <div className="lp-wave">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
            <path d="M0,48C360,80,720,16,1440,48L1440,80L0,80Z" fill="#f8fafc" />
          </svg>
        </div>
      </section>

      {/* ▸ Marquee */}
      <div className="lp-mq">
        <div className="lp-mq-track">
          {[...MQ, ...MQ].map((w, i) => (
            <React.Fragment key={i}>
              <span className="lp-mq-item">{w}</span>
              <span className="lp-mq-dot">◆</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ▸ Modules */}
      <section className="lp-mod" id="portals" ref={modRef} onMouseMove={onModMove}>
        <div className="lp-mod-in">

          <div className="lp-sh sr sr-up">
            <span className="lp-tag">Portals</span>
            <h2 className="lp-sh-h">Choose Your Portal</h2>
            <p className="lp-sh-p">
              Access your dedicated workspace with role-specific tools
              designed for maximum productivity.
            </p>
          </div>

          <div className="lp-grid">
            {/* Admin */}
            <Tilt className="lp-card lp-card--admin sr sr-tilt d1">
              <div className="lp-card-glow" />
              <div className="lp-card-body">
                <span className="lp-card-num">01</span>
                <div className="lp-ci lp-ci--admin"><ShieldCheck size={28} /></div>
                <h2 className="lp-ct">Admin Portal</h2>
                <p className="lp-cd">
                  Centralized control for managers. Oversee vendor lifecycles,
                  approve registrations, manage contracts, and access analytics.
                </p>
                <div className="lp-fs">
                  <F icon={Users}    text="User & Role Management" t="admin" />
                  <F icon={BarChart3} text="Performance Analytics"  t="admin" />
                  <F icon={FileText}  text="Compliance Monitoring"  t="admin" />
                </div>
                <button className="lp-btn lp-btn--admin" onClick={() => onNavigateAdmin?.()}>
                  <span>Access Admin Login</span><ArrowRight size={18} />
                </button>
              </div>
            </Tilt>

            {/* Vendor */}
            <Tilt className="lp-card lp-card--vendor sr sr-tilt d2">
              <div className="lp-card-glow" />
              <div className="lp-card-body">
                <span className="lp-card-num">02</span>
                <div className="lp-ci lp-ci--vendor"><Store size={28} /></div>
                <h2 className="lp-ct">Partner Portal</h2>
                <p className="lp-cd">
                  For suppliers and partners. Manage business profiles, submit
                  bids, track purchase orders, and process invoices.
                </p>
                <div className="lp-fs">
                  <F icon={PackageCheck} text="Order Fulfillment"  t="vendor" />
                  <F icon={Truck}        text="Shipment Tracking"  t="vendor" />
                  <F icon={CreditCard}   text="Invoice Management" t="vendor" />
                </div>
                <button className="lp-btn lp-btn--vendor" onClick={() => onNavigateVendor?.()}>
                  <span>Access Partner Login</span><ArrowRight size={18} />
                </button>
              </div>
            </Tilt>

            {/* Recruiter */}
            <Tilt className="lp-card lp-card--recruiter sr sr-tilt d3">
              <div className="lp-card-glow" />
              <div className="lp-card-body">
                <span className="lp-card-num">03</span>
                <div className="lp-ci lp-ci--recruiter"><Briefcase size={28} /></div>
                <h2 className="lp-ct">Recruiter Portal</h2>
                <p className="lp-cd">
                  Workspace for hiring partners. Browse open requisitions,
                  submit student profiles, and track hiring progress.
                </p>
                <div className="lp-fs">
                  <F icon={Briefcase} text="Job Board Access"      t="recruiter" />
                  <F icon={UserPlus}  text="Submit Candidates"     t="recruiter" />
                  <F icon={Calendar}  text="Interview Scheduling"  t="recruiter" />
                </div>
                <button className="lp-btn lp-btn--recruiter" onClick={() => onNavigateRecruiter?.()}>
                  <span>Access Recruiter Login</span><ArrowRight size={18} />
                </button>
              </div>
            </Tilt>

            {/* Candidate */}
            <Tilt className="lp-card lp-card--candidate sr sr-tilt d4">
              <div className="lp-card-glow" />
              <div className="lp-card-body">
                <span className="lp-card-num">04</span>
                <div className="lp-ci lp-ci--candidate"><GraduationCap size={28} /></div>
                <h2 className="lp-ct">Candidate Portal</h2>
                <p className="lp-cd">
                  For job seekers. Create your profile, apply for jobs, take
                  assessments, and track your application status.
                </p>
                <div className="lp-fs">
                  <F icon={Search}        text="Find & Apply for Jobs"     t="candidate" />
                  <F icon={Award}         text="Track Application Status"  t="candidate" />
                  <F icon={GraduationCap} text="Skill Assessments"         t="candidate" />
                </div>
                <button className="lp-btn lp-btn--candidate" onClick={() => onNavigateCandidate?.()}>
                  <span>Candidate Login</span><ArrowRight size={18} />
                </button>
              </div>
            </Tilt>
          </div>
        </div>
      </section>

      {/* ▸ Footer */}
      <footer className="lp-ft">
        <div className="lp-ft-in">
          <div className="lp-ft-br">
            <div className="lp-ft-box"><ShieldCheck size={16} color="#fff" /></div>
            <span>TalentOza</span>
          </div>
          <p className="lp-ft-cp">&copy; 2025 TalentOza. All rights reserved.</p>
          <div className="lp-ft-ls">
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
}